import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import PrintIcon from "@mui/icons-material/Print";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { datadogRum } from "@datadog/browser-rum";

import { useAppSelector } from "app/hooks";
import Dialog from "components/dialog";
import {
  PDF_PRINTABLE_TABLE_LAYOUT_FIXES,
  VERTICAL_SOUTHWESTERN_LIMITATIONS
} from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import type { Patient } from "services/types/patients.type";
import { openFile } from "utils/common";

import { createAfterVisitSummaryPdf } from "./after-visit-summary-pdf.util";

type AfterVisitSummaryPrintDialogProps = {
  /** The letter as HTML — pass it through `getAfterVisitSummaryHtml` first. */
  letterHtml: string;
  patient: Patient | undefined;
  onClose: () => void;
  /** Raise above a host dialog that lifted its own z-index (the scribe details modal). */
  zIndex?: number;
};

/**
 * Preview and print the After Visit Summary letter.
 *
 * Takes no `open` prop — the caller mounts it only while a preview is wanted, so every open builds
 * a fresh blob from the current letter rather than showing whatever the last open rendered.
 *
 * The preview is an `<iframe>` over the blob rather than the react-pdf canvas, matching the
 * template and custom-print-form previews: it brings the browser's own zoom, paging, print and
 * download for free, and it is the fallback if the Print button's pop-up is blocked.
 */
export default function AfterVisitSummaryPrintDialog({
  letterHtml,
  patient,
  onClose,
  zIndex
}: Readonly<AfterVisitSummaryPrintDialogProps>) {
  const flags = useFlags();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);

  // Both derived the same way the patient-documents print link derives them, so a printed letter
  // and a printed document render tables identically.
  const printableTableLayoutFixesEnabled = Boolean(
    flags[PDF_PRINTABLE_TABLE_LAYOUT_FIXES]
  );
  const pdfNativeTablesEnabled = Boolean(
    currentUser?.native_pdf_tables_enabled ||
      flags[VERTICAL_SOUTHWESTERN_LIMITATIONS]
  );

  const [addPracticeLogo, setAddPracticeLogo] = useState(true);
  const [addPatientHeader, setAddPatientHeader] = useState(true);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  // The URL currently handed to the iframe, so a rebuild can revoke exactly the one it replaces
  // without `blobUrl` having to be an effect dependency (which would re-run the build forever).
  const blobUrlRef = useRef<string | null>(null);
  const [buildToken, setBuildToken] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    setIsBuilding(true);
    setHasFailed(false);

    createAfterVisitSummaryPdf({
      letterHtml,
      patient,
      clinic: currentClinic,
      staffMember: currentUser,
      addPracticeLogo,
      addPatientHeader,
      printableTableLayoutFixesEnabled,
      pdfNativeTablesEnabled
    })
      .then((blob) => {
        if (!isCurrent) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch((error) => {
        if (!isCurrent) return;
        // Never leave a stale blob on screen presented as the current document — clear it and say
        // so, so nobody prints the previous checkbox combination believing it is this one.
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        setBlobUrl(null);
        setHasFailed(true);
        datadogRum.addError(error, {
          context: { source: "after_visit_summary_pdf_generation" }
        });
      })
      .finally(() => {
        if (isCurrent) setIsBuilding(false);
      });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterHtml, addPracticeLogo, addPatientHeader, buildToken]);

  // Release the blob when the dialog goes away; the iframe is gone with it.
  useEffect(
    () => () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    []
  );

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    // The shared helper, which the invoice/statement/receipt paths use. It opens a tab and prints;
    // a blocked pop-up makes it a no-op with no error, which is why the preview above stays on
    // screen — the browser's own print control in the iframe is the way out.
    openFile(blobUrl, true);
  }, [blobUrl]);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Print After Visit Summary"
      // Wide enough to show an A4 page at a readable size: at the 128 dpi this document renders
      // at, the page is ~1058pt across, so a narrower dialog scales the preview down to where the
      // letterhead is unreadable and nobody can actually check it. Same width as the
      // custom-print-form preview, which shows the same kind of page.
      minWidth={1324}
      maxWidth="xl"
      sx={zIndex ? { zIndex } : undefined}
    >
      <Stack gap={1.5} data-testid="avs-print-dialog">
        <Stack direction="row" gap={3} flexWrap="wrap">
          <FormControlLabel
            label="Add Practice Logo"
            control={
              <Checkbox
                checked={addPracticeLogo}
                onChange={(event) => setAddPracticeLogo(event.target.checked)}
                data-testid="avs-print-add-practice-logo"
              />
            }
          />
          <FormControlLabel
            label="Add Patient Header"
            control={
              <Checkbox
                checked={addPatientHeader}
                onChange={(event) => setAddPatientHeader(event.target.checked)}
                data-testid="avs-print-add-patient-header"
              />
            }
          />
        </Stack>

        <Box
          sx={{
            height: "60vh",
            minHeight: 400,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {isBuilding && <CircularProgress size={28} />}
          {!isBuilding && hasFailed && (
            <Stack gap={2} alignItems="center">
              <Typography data-testid="avs-print-error">
                We could not build the letter for printing.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => setBuildToken((token) => token + 1)}
              >
                Try again
              </Button>
            </Stack>
          )}
          {!isBuilding && !hasFailed && blobUrl && (
            <iframe
              src={blobUrl}
              title="After Visit Summary print preview"
              style={{ border: "none", width: "100%", height: "100%" }}
              data-testid="avs-print-preview"
            />
          )}
        </Box>

        <Stack direction="row" justifyContent="flex-end" gap={1.5}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon sx={{ width: 16 }} />}
            onClick={handlePrint}
            disabled={isBuilding || !blobUrl}
            data-testid="avs-print-confirm"
          >
            Print
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
